// docs/21 §7 the Saga: what the World knows about itself. The realm (epithet and saying), the three
// ages, the places and the haven, the factions and what they want, the campaign (premise, the
// villain by name and title, the mystery; the secret never reaches the client) with the arc's five
// stages as a track where only the stages reached are lit, the World's first namedFoe once the
// opening has been fought (docs/22 O4), the storybook's chapters so far with a replay of each
// chapter's scene (docs/86), and a replay of the intro.
import { Badge, Button, Modal, SeasonGlyph } from '../../components';
import { useSession } from '../../../core/stores/session';
import { useBookChapters, useOpeningNamedFoe, useWorldClock, useWorldOpening } from '../../../core/subscriptions/hooks';
import { display, OPENING, SOURCE, seasonIndex, storyDate, wonRow, type Genesis } from '../../../core/story/genesis';
import { BIOME_WORDS, SEASON_KEYS, dateLine, elapsed, type ClockRow } from '../../../core/story/calendar';
import { CHAPTER, chapterLabel, QUESTS_PER_CHAPTER } from '../../../core/story/chapter';
import './intro.css';

const STAGES = ['Opening', 'Development', 'Complication', 'Escalation', 'Resolution'];

/** The first namedFoe as the ledger knows them (docs/22 O4); absent until the opening is fought. */
export interface SagaNamedFoe { live: boolean; survivals: number; lastLine: string; armorKnown: boolean }

/** The realm's calendar (docs/23): the reckoning, the four seasons by name, today. */
function SagaCalendar({ g, clock }: { g: Genesis; clock?: ClockRow }) {
  const now = clock ? clock.season : seasonIndex(g.calendar.season);
  return (
    <div className="fk-saga__calendar" data-testid="saga-calendar">
      <p className="fk-saga__reckoning">
        {clock ? <>Today is the <b>{dateLine(clock)}</b>; the tale has run {elapsed(clock)}.</> : <>The tale begins in the <b>{storyDate(g.calendar)}</b>.</>}
        {' '}{BIOME_WORDS[(g.realm.biome ?? '').toLowerCase()] ?? g.realm.biome}.
      </p>
      <ol className="fk-saga__seasons">
        {g.calendar.seasons.map((name, i) => (
          <li key={i} className={i === now ? 'is-now' : ''}><SeasonGlyph season={SEASON_KEYS[i]!} scale={2} /><b>{name}</b><small>{SEASON_KEYS[i]}</small></li>
        ))}
      </ol>
    </div>
  );
}

/** A chapter of the storybook as the Saga lists it (docs/86, a `book_chapter` row). */
export interface SagaChapter { number: number; title: string; goal: string; summary: string; state: number; hasScene: boolean; met: number; missed: number }

/** The storybook (docs/86): every chapter so far, the one being played lit, each scene replayable. */
function SagaBook({ g, book, onReplayChapter }: { g: Genesis; book: SagaChapter[]; onReplayChapter?: (n: number) => void }) {
  return (
    <ol className="fk-saga__book" data-testid="saga-book">
      {book.map((c) => {
        const now = c.state !== CHAPTER.CLOSED;
        const played = c.met + c.missed;
        return (
          <li key={c.number} className={now ? 'is-now' : ''} data-testid="saga-chapter">
            <span className="fk-saga__book-head">
              <span className="fk-saga__book-name">{chapterLabel(c.number, c.title)}</span>
              {now && <Badge tone="gold">{c.state === CHAPTER.CLOSING ? 'being told' : `quest ${Math.min(played + 1, QUESTS_PER_CHAPTER)} of ${QUESTS_PER_CHAPTER}`}</Badge>}
              {c.hasScene && onReplayChapter && <Button small variant="ghost" onClick={() => onReplayChapter(c.number)} data-testid="saga-chapter-replay">Replay</Button>}
              {c.number === 1 && onReplayChapter && !c.hasScene && <span className="fk-saga__kind">opened by the intro</span>}
            </span>
            <span className="fk-saga__book-text">{c.summary.trim() ? c.summary : c.goal || g.arc.stages[0]}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function SagaBody({ g, source, reached = 1, onReplay, namedFoe, clock, book = [], onReplayChapter }: { g: Genesis; source: number; reached?: number; onReplay?: () => void; namedFoe?: SagaNamedFoe; clock?: ClockRow; book?: SagaChapter[]; onReplayChapter?: (n: number) => void }) {
  const places: Array<[string, { name: string; text: string }]> = [['Haven', g.haven], ['The wild', g.places.wild], ['The road', g.places.road], ['The crossing', g.places.crossing]];
  return (
    <div className="fk-saga" data-testid="saga" data-sfx-scroll>
      <header className="fk-saga__head">
        <h2 className="fk-saga__realm" data-testid="saga-realm">{display(g.realm.name)}</h2>
        <p className="fk-saga__epithet">{display(g.realm.epithet)}</p>
        <p className="fk-saga__saying">{g.realm.saying}</p>
        <p className="fk-saga__source" data-testid="saga-source">{source === SOURCE.STORYTELLER ? '✒ Told by the storyteller for this World alone' : '❦ From the old tales (the storyteller was away)'} · <Badge tone="plum">{g.realm.tone}</Badge></p>
      </header>
      <div className="fk-saga__cols">
        <section className="fk-saga__section">
          {g.calendar && <>
            <h3 className="fk-saga__h">The reckoning · {g.calendar.reckoning || 'the years'}</h3>
            <SagaCalendar g={g} clock={clock} />
          </>}
          <h3 className="fk-saga__h">The three ages</h3>
          <ol className="fk-saga__ages">{g.ages.map((a, i) => <li key={i}><span className="fk-saga__age-name">{a.name}</span><span>{a.text}</span></li>)}</ol>
          <h3 className="fk-saga__h">Places</h3>
          <dl className="fk-saga__places">{places.map(([k, p]) => <div key={k}><dt><span className="fk-saga__kind">{k}</span>{display(p.name)}</dt><dd>{p.text}</dd></div>)}</dl>
        </section>
        <section className="fk-saga__section">
          {book.length > 0 && <>
            <h3 className="fk-saga__h">The storybook</h3>
            <SagaBook g={g} book={book} onReplayChapter={onReplayChapter} />
          </>}
          <h3 className="fk-saga__h">The campaign · {g.arc.shape}</h3>
          <p className="fk-saga__premise">{g.arc.premise}</p>
          <div className="fk-saga__villain"><span className="fk-saga__villain-name">{display(g.arc.antagonist.name)}</span><span className="fk-saga__villain-title">{g.arc.antagonist.title}</span></div>
          <p className="fk-saga__mystery"><span>The question</span>{g.arc.mystery}</p>
          <ol className="fk-saga__track" aria-label="The arc">
            {g.arc.stages.map((s, i) => (
              <li key={i} className={i < reached ? 'on' : ''} data-testid="saga-stage">
                <span className="fk-saga__stage-name">{STAGES[i] ?? `Stage ${i + 1}`}</span>
                <span className="fk-saga__stage-text">{i < reached ? s : '…'}</span>
              </li>
            ))}
          </ol>
          {namedFoe && <>
            <h3 className="fk-saga__h">Your first named foe</h3>
            <div className="fk-saga__villain fk-saga__named-foe" data-testid="saga-named-foe">
              <span className="fk-saga__villain-name">{display(g.battle.leader.name)}</span>
              <span className="fk-saga__villain-title">{g.battle.leader.title} · {namedFoe.live ? (namedFoe.survivals <= 1 ? 'has survived you once' : `has survived you ${namedFoe.survivals} times`) : 'fallen at last'}</span>
              {namedFoe.lastLine && <q className="fk-saga__vow">{namedFoe.lastLine}</q>}
              {namedFoe.armorKnown && <span className="fk-saga__armor"><b>Why they cannot fall:</b> {g.battle.named_foe.armor}</span>}
            </div>
          </>}
          <h3 className="fk-saga__h">Who wants what</h3>
          <ul className="fk-saga__factions">{g.factions.map((f, i) => <li key={i}><span className="fk-saga__faction">{f.name}</span>{f.want}</li>)}</ul>
        </section>
      </div>
      {onReplay && <div className="fk-saga__foot"><Button onClick={onReplay} data-testid="saga-replay">Replay the intro</Button></div>}
    </div>
  );
}

export function SagaModal({ open, onClose, g, source, onReplay, onReplayChapter }: { open: boolean; onClose: () => void; g: Genesis | undefined; source: number; onReplay: () => void; onReplayChapter?: (n: number) => void }) {
  const worldId = useSession((s) => s.worldId);
  const opening = useWorldOpening(worldId);
  const row = useOpeningNamedFoe(worldId);
  const clock = useWorldClock(worldId);
  const chapters = useBookChapters(worldId);
  const book: SagaChapter[] = chapters.map((c) => ({ number: c.number, title: c.title, goal: c.goal, summary: c.summary, state: c.state, hasScene: c.scene.length > 0, met: c.met, missed: c.missed }));
  const done = opening?.phase === OPENING.DONE;
  // the armour is told only once they have been beaten and got away (docs/22 O4)
  const namedFoe = row && done ? { live: row.live, survivals: row.survivals, lastLine: row.lastLine, armorKnown: wonRow(opening?.outcomeRow) } : undefined;
  return (
    <Modal open={open && !!g} onClose={onClose} eyebrow="The Saga" title={g ? display(g.realm.name) : ''} size="wide" testId="saga-modal">
      {g && <SagaBody g={g} source={source} onReplay={onReplay} namedFoe={namedFoe} clock={clock} book={book} onReplayChapter={onReplayChapter}
        // docs/86: one arc stage a storybook chapter
        reached={Math.min(5, book[book.length - 1]?.number ?? 1)} />}
    </Modal>
  );
}
